import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Tooltip } from "@mui/material";
import {
  calculateDefaultRowViewState,
  displayDate,
  displayISODateLoose,
  isNotUndefined,
  numberCellValueFromGQL,
  parseDate,
  UNGROUPED_CELL_ID,
  RowViewState,
} from "Backend";
import { CellAcc, CellWithAccs, FormFieldContext, GroupedValueGroup } from "contexts/FormFieldContext";
import { useContext, useState } from "react";
import { P } from "theme/typography";
import displayCellValue from "./displayCellValue";
import { Cell, CellType } from "generated/graphql";
import PSelect from "components/PSelect";
import { EntryTable } from "./EntryTable";
import { AddCircle, ArrowDropDown, ArrowRight, Info } from "@mui/icons-material";
import theme, { EllipsisSX } from "theme";
import { useAppSelector } from "redux/hooks";
import { selectCaseInfo } from "redux/slices/case";
import { ageAtDate } from "helpers/ageAtDate";
import { splitMultiSelectCellAccs } from "./form-field-helpers";
import { pluralize } from "helpers/coll";
import { LightTooltip } from "theme/Tooltips";
import { TagFilters } from "components/BetterResponseTable/TagFilters";
import { useFilteredValueGroups } from "helpers/hooks/useFilteredValueGroups";
import { EntryContext } from "components/tags/types";
import { ContextDialog } from "components/ContextDialog/ContextDialog";
import { ContextMode } from "components/ContextDialog/types";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import { EntryTableExpander } from "./EntryTableExpander";

function NumberRange({ cellAccs }: { cellAccs: CellAcc[] }) {
  const numberValues = cellAccs.map((cellAcc) => numberCellValueFromGQL(cellAcc.cellValue)).filter(isNotUndefined);
  return (
    <P variant="xs">
      {Math.min(...numberValues)} - {Math.max(...numberValues)}
    </P>
  );
}

function CellAccTooltip({ text, cellAccs, showCounts }: { text: string; cellAccs: CellAcc[]; showCounts: boolean }) {
  return cellAccs.length > 0 ? (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
      <P variant="xs">{text}</P>
      <LightTooltip
        title={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 2 }}>
            {cellAccs.map((cellAcc, index) => (
              <P variant="xs" key={index}>
                {displayCellValue(cellAcc.cellValue)} {showCounts ? `(${cellAcc.count})` : ""}
              </P>
            ))}
          </Box>
        }>
        <Info fontSize="small" sx={{ color: theme.palette.grey[200], fontSize: 14 }} />
      </LightTooltip>
    </Box>
  ) : (
    <></>
  );
}

function CellAccDisplay({ originalCell, cellAccs }: { originalCell: Cell; cellAccs: CellAcc[] }) {
  const cellAccsToUse = originalCell.type === CellType.MultiSelect ? splitMultiSelectCellAccs(cellAccs) : cellAccs;
  const cell = cellAccsToUse[0]?.cell;
  if (cell === undefined) {
    return <></>;
  }
  const displayableCellAccs = cellAccsToUse.slice(0, 5);
  const extraCellAccs = cellAccsToUse.length > 5 ? cellAccsToUse.slice(5) : [];
  const showCounts = cellAccsToUse.some((cellAcc) => cellAcc.count > 1);

  return cell.type === CellType.Select || cellAccs.length < 3 ? (
    <>
      {displayableCellAccs.map((cellAcc, index) => (
        <P variant="xs" key={index}>
          {displayCellValue(cellAcc.cellValue)} {showCounts ? `(${cellAcc.count})` : ""}
          {index === cellAccs.length - 1 ? "" : ", "}
        </P>
      ))}
      <CellAccTooltip text={`... and ${extraCellAccs.length} more`} cellAccs={extraCellAccs} showCounts={showCounts} />
    </>
  ) : cell.type === CellType.Float || cell.type === CellType.Integer ? (
    <NumberRange cellAccs={cellAccsToUse} />
  ) : (
    <CellAccTooltip text={`${cellAccs.length} values`} cellAccs={cellAccsToUse} showCounts={showCounts} />
  );
}

function CellLists({ cellWithAccs, groupByCellId }: { cellWithAccs: CellWithAccs; groupByCellId?: number }) {
  return cellWithAccs.accs.length > 0 && cellWithAccs.cell.id !== groupByCellId ? (
    <Box sx={{ display: "flex", gap: 1 }}>
      <P variant="xsBold">{cellWithAccs.cell.name}: </P>
      <CellAccDisplay originalCell={cellWithAccs.cell} cellAccs={cellWithAccs.accs} />
    </Box>
  ) : (
    <></>
  );
}

function ResponseGroup({
  group,
  activeSectionId,
  onReferenceClick,
}: {
  group: GroupedValueGroup;
  activeSectionId?: number;
  onReferenceClick: (context: EntryContext) => void;
}) {
  const ffContext = useContext(FormFieldContext);
  const expanded = ffContext.fieldUIState?.expandedGroupNames?.includes(group.cellValue) ?? false;
  const startDate = group.sortedDates[0];
  const endDate = group.sortedDates[group.sortedDates.length - 1];
  const startDateEqualsEndDate = startDate?.getTime() === endDate?.getTime();
  const caseInfo = useAppSelector(selectCaseInfo);

  const dob = parseDate(caseInfo?.dateOfBirth);
  const dateRange = startDateEqualsEndDate
    ? displayISODateLoose(startDate)
    : `${displayISODateLoose(startDate)} - ${displayISODateLoose(endDate)}`;
  const fullDateRange = startDateEqualsEndDate
    ? displayDate(startDate)
    : `${displayDate(startDate)} - ${displayDate(endDate)}`;
  const age =
    dob !== undefined && startDate !== undefined && endDate !== undefined
      ? startDateEqualsEndDate
        ? ageAtDate(dob, startDate)
        : `${ageAtDate(dob, startDate)} - ${ageAtDate(dob, endDate)}`
      : "";

  const firstGroupWithReference = group.valueGroups.find((valueGroup) => valueGroup.references.length > 0);

  const handleViewReferences = (evt: React.MouseEvent) => {
    evt.preventDefault();
    evt.stopPropagation();
    const reference = firstGroupWithReference?.references[0];
    if (firstGroupWithReference && reference) {
      onReferenceClick({ valueGroupId: firstGroupWithReference.id, referenceId: reference.id });
    }
  };

  const groupName = group.cellValue || `No ${ffContext.groupByCell?.name ?? "Data"}`;

  const setExpanded = () => {
    if (expanded) {
      ffContext.collapseGroupName(group.cellValue);
    } else {
      ffContext.expandGroupName(group.cellValue);
    }
  };

  return (
    <Box
      sx={{
        my: 1,
        borderBottom: `1px solid ${theme.palette.grey[50]}`,
        pb: 2,
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          ":hover": { backgroundColor: theme.palette.grey[50] },
          cursor: "pointer",
        }}
        onClick={setExpanded}>
        <div className="flex gap-1 mb-1 items-center">
          <div className="flex gap-2 grow">
            <IconButton
              sx={{
                p: 0,
              }}
              onClick={(evt) => {
                evt.stopPropagation();
                setExpanded();
              }}>
              {expanded ? <ArrowDropDown /> : <ArrowRight />}
            </IconButton>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <P variant="lgBold" sx={EllipsisSX} title={group.cellValue}>
                {groupName}
              </P>
              <P variant="sm" sx={{ ml: 0.5 }}>
                ({group.valueGroups.length})
              </P>
            </Box>
          </div>
          <div>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Tooltip title={fullDateRange}>
                <P variant="baseBold">{dateRange}</P>
              </Tooltip>
              {dob !== undefined && startDate !== undefined && endDate !== undefined && <P variant="sm">(Age {age})</P>}
            </Box>
          </div>
          <div>
            <Tooltip
              title={
                firstGroupWithReference
                  ? `Show first ${groupName} response with references `
                  : `No ${groupName} response with references`
              }>
              <span>
                <Button
                  onClick={handleViewReferences}
                  disabled={!firstGroupWithReference}
                  data-pendo="profile-grouped-res-view-references">
                  View References
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 1, pb: 1 }}>
          {group.cellAccs.map((cellWithAccs, index) => {
            return <CellLists key={index} cellWithAccs={cellWithAccs} groupByCellId={ffContext.groupByCell?.id} />;
          })}
        </Box>
      </Box>
      <EntryTable
        valueGroups={group.valueGroups}
        hideCellSelector
        groupByCellToHide={ffContext.groupByCellIdHandle[0]}
        group={group}
        groupName={groupName}
        activeSectionId={activeSectionId}
        onReferenceClick={onReferenceClick}
        expanded={expanded}
        setExpanded={setExpanded}
        showExpander={false}
      />
    </Box>
  );
}

function getDefaultExpanded(
  groupExpanded: boolean | undefined,
  ungrouped: boolean,
  valueGroupCount: number,
  groupedValueGroupCount: number
) {
  if (groupExpanded !== undefined) return groupExpanded;
  if (ungrouped && valueGroupCount > 25) return false;
  if (groupedValueGroupCount > 15) return false;

  return true;
}

export default function GroupedEntries({ defaultEntryContext }: { defaultEntryContext?: EntryContext }) {
  const { tagFilters, setTagFilters, groupedAndFilteredValueGroups, filteredValueGroups, availableTags, tagCounts } =
    useFilteredValueGroups();
  const ffContext = useContext(FormFieldContext);
  const ungrouped = ffContext.groupByCellIdHandle[0] === UNGROUPED_CELL_ID;
  const valueGroupCount = filteredValueGroups.length;
  const [entryContext, setEntryContext] = useState<EntryContext | undefined>(defaultEntryContext);
  const { searchParams, updateParams } = useSearchParamsWithUpdate();

  const setContextAndUpdateUrlParams: React.Dispatch<React.SetStateAction<EntryContext | undefined>> = (context) => {
    const nextContext = typeof context === "function" ? context(entryContext) : context;
    const shouldReplace = ffContext.formField.id.toString() === searchParams.get("fieldId");
    updateParams(
      [
        { key: "fieldId", value: ffContext.formField.id.toString() },
        { key: "valueGroupId", value: nextContext?.valueGroupId.toString() },
        { key: "referenceId", value: nextContext?.referenceId?.toString() },
        { key: "page", value: nextContext?.page?.toString() },
      ],
      { replace: shouldReplace }
    );
    setEntryContext(nextContext);
  };

  const [expanded, setExpanded] = useState(
    getDefaultExpanded(
      ffContext.fieldUIState?.groupExpanded,
      ungrouped,
      valueGroupCount,
      groupedAndFilteredValueGroups.length
    )
  );

  const showRegularTable = ungrouped && ffContext.valueGroups.length > 0;
  const showGroupedTable = !showRegularTable && expanded;

  const groupCount = groupedAndFilteredValueGroups.length;
  const entryCount = groupedAndFilteredValueGroups.map((g) => g.valueGroups).flat().length;
  const expandText = (
    <div className="flex items-center gap-1">
      <span className="font-semibold">
        {groupCount} {pluralize(groupCount, "Group")}
      </span>
      <span>
        ({entryCount} {pluralize(entryCount, "Entry", "Entries")})
      </span>
    </div>
  );

  const addValueGroup = () => {
    ffContext.groupByCellIdHandle[1](UNGROUPED_CELL_ID);
    ffContext.addValueGroup();
  };

  return (
    <>
      {ffContext.valueGroups.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 mb-2 mt-4">
          <FormControl sx={{ mb: 0.5 }}>
            <InputLabel>Group by</InputLabel>
            <PSelect
              label="Group by"
              data-pendo="field-group-by-cell"
              value={ffContext.groupByCellIdHandle[0]}
              onChange={(e) => {
                const cellId = e.target.value as number;
                const valueGroupIds = ffContext.valueGroups.map((vg) => vg.id);
                ffContext.groupByCellIdHandle[1](cellId);
                ffContext.setValueGroupViewStateBulk(
                  valueGroupIds,
                  cellId === UNGROUPED_CELL_ID
                    ? calculateDefaultRowViewState(ffContext.valueGroups.length)
                    : RowViewState.ReadCollapsed
                );
              }}>
              <MenuItem value={UNGROUPED_CELL_ID}>None</MenuItem>
              {ffContext.validGroupByCells.map((cell, index) => (
                <MenuItem key={index} value={cell.id}>
                  {cell.name}
                </MenuItem>
              ))}
            </PSelect>
          </FormControl>
          <TagFilters
            availableTags={availableTags}
            tagFilters={tagFilters}
            setTagFilters={(filters) => {
              setTagFilters(filters);
              setExpanded(true);
            }}
            tagCounts={tagCounts}
          />
        </div>
      ) : (
        <Button
          color="default"
          id={"pfield_add_response_" + ffContext.formField.id}
          onClick={addValueGroup}
          startIcon={<AddCircle />}>
          Add Entry
        </Button>
      )}

      {showRegularTable && (
        <EntryTable
          valueGroups={filteredValueGroups}
          onReferenceClick={setContextAndUpdateUrlParams}
          expanded={expanded}
          setExpanded={setExpanded}
        />
      )}
      {!showRegularTable && !ungrouped && ffContext.valueGroups.length > 0 && (
        <div className="pl-2 mt-2 max-w-fit">
          <EntryTableExpander text={expandText} expanded={expanded} setExpanded={setExpanded} />
        </div>
      )}
      {showGroupedTable && (
        <div className="flex flex-col gap-2">
          {groupedAndFilteredValueGroups.map((group) => (
            <ResponseGroup key={group.cellValue} group={group} onReferenceClick={setContextAndUpdateUrlParams} />
          ))}
        </div>
      )}
      {entryContext && (
        <ContextDialog
          open={true}
          mode={ContextMode.EntryViewer}
          onClose={() => setContextAndUpdateUrlParams(undefined)}
          componentProps={{
            entryContext,
            setEntryContext: setContextAndUpdateUrlParams,
            onClose: () => setContextAndUpdateUrlParams(undefined),
            isLinkable: true,
          }}
        />
      )}
    </>
  );
}
